import {
  Button,
  CadencePicker,
  DayOfWeek,
  Interval,
  Medium,
  Padding,
  Spacer,
  parseTimeString,
  useToasts,
} from "@vestaboard/installables";
import { useEffect, useState } from "react";

import { GRAPHQL_ENDPOINT } from "../constants";
import { ISubscription } from "../types";
import { makeStyles } from "@mui/styles";
import { useExchangeSubscriptionConfigToken } from "@vestaboard/installables/lib/hooks/useExchangeSubscriptionConfigToken";

interface ISettings {
  subscription: ISubscription;
  subscriptionConfigurationToken: string;
  updateSubscription: (subscription: ISubscription) => Promise<void>;
}

const useStyles = makeStyles({
  line: {
    height: 1,
    width: "100%",
    background: "#333",
  },
});

export const Settings = (props: ISettings) => {
  const classes = useStyles();
  const [isTwentyFourHourTime, setIsTwentyFourHourTime] = useState(false);
  const [subscription, setSubscription] = useState(props.subscription);
  const { token, exchange } = useExchangeSubscriptionConfigToken();
  const { addToast } = useToasts();

  useEffect(
    () => {
      exchange(props.subscriptionConfigurationToken);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.subscriptionConfigurationToken],
  );

  useEffect(() => {
    if (!token) {
      return;
    }

    fetch(GRAPHQL_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-vestaboard-subscription-token": token,
      },
      body: JSON.stringify({
        query: `query GetBoard($id: String!) {
          subscription: subscriptionV2(id: $id) {
            ... on SubscriptionErrorV2 {
              type
              error
            }
            ... on SubscriptionV2 {
              id
              subscriptionBoardConnection {
                board {
                  id
                  title
                  isTwentyFourHourTime
                }
              }
            }
          }
        }`,
        variables: {
          id: props.subscription.id,
        },
      }),
    })
      .then((res) => res.json())
      .then((res) => {
        if (res.data?.subscription?.subscriptionBoardConnection?.board) {
          setIsTwentyFourHourTime(
            res.data.subscription.subscriptionBoardConnection.board
              .isTwentyFourHourTime,
          );
        }
      })
      .catch(console.error);
  }, [props.subscription.id, token]);

  return (
    <Padding>
      <Spacer size="medium" />
      <Medium>
        Be surprised with a new message from your Blended content at the
        following frequency.
      </Medium>
      <Spacer size="large" />
      <div className={classes.line} />
      <Spacer size="large" />
      <CadencePicker
        key={`kp-${isTwentyFourHourTime ? 1 : 0}`}
        isTwentyFourHourTime={isTwentyFourHourTime}
        allowWeekdaySelection
        defaultDays={[
          DayOfWeek.Monday,
          DayOfWeek.Tuesday,
          DayOfWeek.Wednesday,
          DayOfWeek.Thursday,
          DayOfWeek.Friday,
          DayOfWeek.Saturday,
          DayOfWeek.Sunday,
        ]}
        intervals={
          [
            "Day",
            "Hour",
            "Hour_2",
            "Hour_4",
            "Minute_1",
            "Minutes_5",
            "Minutes_10",
            "Minutes_15",
            "Minutes_20",
            "Minutes_30",
            "Minutes_2",
            "Minutes_3",
            "Minutes_4",
          ] as any
        }
        cadence={
          subscription.cadence || {
            interval: Interval.Hour,
            startAtTime: parseTimeString("09:00"),
            endAtTime: parseTimeString("17:00"),
            daysOfTheWeek: [
              DayOfWeek.Monday,
              DayOfWeek.Tuesday,
              DayOfWeek.Wednesday,
              DayOfWeek.Thursday,
              DayOfWeek.Friday,
            ],
          }
        }
        onChange={(cadence) => {
          setSubscription({
            ...subscription,
            cadence,
          });
        }}
      />
      <Spacer size="extraLarge" />
      <Button
        onClick={async () => {
          try {
            await props.updateSubscription(subscription);
            addToast("Your settings were saved successfully", {
              appearance: "success",
            });
          } catch {
            addToast("Your settings could not be saved", {
              appearance: "error",
            });
          }
        }}
        buttonType="white"
      >
        Save
      </Button>
      <Spacer size="extraLarge" />
    </Padding>
  );
};
